import { ancillaryBundle, seatMap } from '@eti/products';
import { connect } from 'react-redux';
import { formValueSelector, change as changeFormValue } from 'redux-form';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useProperty, useSearch, useSnowplowTracking } from '@eti/providers';
import PropTypes from 'prop-types';
import { collectFlightsAndPassengersContextData } from '../../../common/tracking/snowplow/snowplowUtils';
import { getTripNotificationChanges } from '../../../utils/notification';
import { useSkyscannerLandingTracking } from '../../../common/tracking/skyscannerTracking';
import { YOUR_SELECTED_TRIP } from '../../../constants/formNamesConstants';
import Loading from '../../../common/loading/components/Loading';
import NoSelectedTripErrorDialog from '../../../common/error-modals/NoSelectedTripErrorDialog';
import YourSelectedTripPage from '../components/YourSelectedTripPage';
import { transformBundleData } from '../../../utils/transformBundleData';
import { SELECT_TRIP } from './mutations';
import { GET_SELECTED_TRIP } from './queries';

const YourSelectedTripPageContainer = ({
  selectedBundleId,
  selectedBundlePrice,
  setSelectedBundleOption,
}) => {
  const history = useHistory();
  const { currentSearchData } = useSearch();
  const { p } = useProperty();
  const [isRefetched, setIsRefetched] = useState(false);
  const [isTripSelected, setIsTripSelected] = useState(false);
  const { isSnowplowTrackingEnabled, updateSnowplowContextData, sendTrackEvent } =
    useSnowplowTracking();
  const [showNoSelectedTripErrorModal, setShowNoSelectedTripErrorModal] = useState(false);
  const [selectedTripData, setSelectedTripData] = useState(null);

  const shouldShowSelectedTrip = history?.location?.state?.shouldShowSelectedTrip;
  const selectionKey = selectedTripData?.selectionKey || window.spa_variables.selectionKey;
  const shouldHideSelectedTrip =
    !shouldShowSelectedTrip || p('YourSelectedTrip.Page.HideSelectedTrip.Enabled');

  const prevBundleSelection = useMemo(() => {
    const prevSelectedBundleOption = selectedTripData?.bundleData?.selectedOption;
    return selectedTripData?.bundleData?.options?.find(({ id }) => id === prevSelectedBundleOption);
  }, [selectedTripData]);

  const prevBundleSelectionProducts = useMemo(() => {
    return prevBundleSelection?.features.map(({ productTypeId }) => productTypeId);
  }, [prevBundleSelection]);

  const { loading: getSelectedTripLoading, refetch } = useQuery(GET_SELECTED_TRIP, {
    variables: { excludedExtraProductIds: [seatMap.id] },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ selectedTrip, ancillaryBundle: bundleData = [] }) => {
      setIsRefetched(false);
      if (selectedTrip) {
        const transformedBundleData = transformBundleData(bundleData[0]);
        const formattedData = {
          ...selectedTrip,
          bundleData: transformedBundleData,
          tripTravelers: selectedTrip.travelers,
        };
        setSelectedTripData(formattedData);
      } else {
        setShowNoSelectedTripErrorModal(true);
      }
    },
    onError: () => {
      setShowNoSelectedTripErrorModal(true);
    },
  });

  const [selectTrip, { loading: isSelectTripLoading }] = useMutation(SELECT_TRIP, {
    onCompleted: ({ selectTrip: selectTripResponse }) => {
      if (!selectTripResponse.success) {
        setShowNoSelectedTripErrorModal(true);
      }
    },
    onError: () => {
      setShowNoSelectedTripErrorModal(true);
    },
  });

  useSkyscannerLandingTracking(selectedTripData);

  useEffect(() => {
    if (isSnowplowTrackingEnabled && selectedTripData && currentSearchData?.routes) {
      updateSnowplowContextData({
        ...collectFlightsAndPassengersContextData(currentSearchData, selectedTripData),
      });

      sendTrackEvent({
        category: 'Your Selected Trip',
        action: 'Seen',
        label: 'Page',
      });
    }
  }, [
    isSnowplowTrackingEnabled,
    currentSearchData,
    selectedTripData,
    sendTrackEvent,
    updateSnowplowContextData,
  ]);

  useEffect(() => {
    if (prevBundleSelection) {
      setSelectedBundleOption(prevBundleSelection);
    }
  }, [setSelectedBundleOption, prevBundleSelection]);

  if (showNoSelectedTripErrorModal) {
    return <NoSelectedTripErrorDialog />;
  }
  if (
    (getSelectedTripLoading || isSelectTripLoading || !selectedTripData) &&
    !isRefetched &&
    !isTripSelected
  ) {
    return <Loading />;
  }

  const selectedExtraProducts = selectedTripData?.availableExtraProducts
    .filter((prod) => {
      const sellSpec = prod?.sellSpecification;
      const hasPrice = sellSpec?.sellPriceTravelers || sellSpec?.price?.price?.value;
      return (
        prod.selectedWithTrip && hasPrice && !prevBundleSelectionProducts?.includes(prod.productId)
      );
    })
    .map((prod) => Number(prod.productId));

  const handleRefetch = () => {
    setIsRefetched(true);
    refetch();
  };
  const handleSelectTrip = async (variables) => {
    setIsTripSelected(true);
    await selectTrip({ ...variables });
  };
  const BrandedFaresVersion = p('YourSelectedTrip.Page.BrandedFares.Version');
  const { brandedFares } = selectedTripData ?? {};
  const shouldDisplayBrandedFares = p('BrandedFares.Enabled') && brandedFares?.length > 0;

  return (
    <YourSelectedTripPage
      {...(selectedTripData ?? {})}
      isSelectTripLoading={isSelectTripLoading}
      isSelectedTripRefetching={isRefetched}
      refetch={handleRefetch}
      selectTrip={
        (BrandedFaresVersion === 2 || BrandedFaresVersion === 3) && shouldDisplayBrandedFares
          ? handleSelectTrip
          : selectTrip
      }
      selectedBundleId={selectedBundleId}
      selectedBundlePrice={selectedBundlePrice}
      selectedExtraProducts={selectedExtraProducts}
      selectionKey={selectionKey}
      setSelectedBundleOption={setSelectedBundleOption}
      shouldHideSelectedTrip={shouldHideSelectedTrip}
      tripChange={getTripNotificationChanges(selectedTripData?.changes, selectedTripData?.bounds)}
    />
  );
};

YourSelectedTripPageContainer.propTypes = {
  selectedBundleId: PropTypes.string,
  selectedBundlePrice: PropTypes.number,
  setSelectedBundleOption: PropTypes.func,
  variables: PropTypes.shape({
    bundleProductSelection: PropTypes.object,
    extraProductSelection: PropTypes.arrayOf(PropTypes.string),
    searchPath: PropTypes.string,
    selectionKey: PropTypes.string,
  }),
};

const mapStateToProps = (state) => {
  const { value } = formValueSelector(YOUR_SELECTED_TRIP)(state, ancillaryBundle.name) || {};
  return {
    selectedBundleId: value?.id,
    selectedBundlePrice: value?.price,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setSelectedBundleOption: (selectedOption) =>
      dispatch(
        changeFormValue(YOUR_SELECTED_TRIP, `${ancillaryBundle.name}.value`, {
          value: selectedOption.type,
          price: selectedOption.priceRaw,
          id: selectedOption.id,
        }),
      ),
  };
};

const connectToStore = connect(mapStateToProps, mapDispatchToProps);

export default connectToStore(YourSelectedTripPageContainer);
